<template>
  <div class="action-toolbar">
    <button class="action-button" @click="copyContent" :title="TEXT.copy">
      <img :src="ICON.copy" :alt="TEXT.copy" class="action-icon" />
    </button>
    <button class="action-button" @click="downloadContent" :title="TEXT.downloadTxt">
      <img :src="ICON.download" :alt="TEXT.downloadTxt" class="action-icon" />
    </button>
    <button class="action-button" @click="emailContent" :title="TEXT.email">
      <img :src="ICON.email" :alt="TEXT.email" class="action-icon" />
    </button>
    <button class="action-button" @click="printContent" :title="TEXT.print">
      <img :src="ICON.print" :alt="TEXT.print" class="action-icon" />
    </button>
    <div class="toolbar-separator"></div>
    <button class="action-button" @click="likeContent" :title="TEXT.like">
      <img :src="ICON.like" :alt="TEXT.like" class="action-icon" />
    </button>
    <button class="action-button" @click="dislikeContent" :title="TEXT.dislike">
      <img :src="ICON.dislike" :alt="TEXT.dislike" class="action-icon" />
    </button>
  </div>
</template>

<script lang="ts" setup>
import store from '@/store/store'
import { ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import { DialogTypes, CustomDialogComponentName } from '@/store/modules/dialog/dialogTypes'
import { useI18n } from 'vue-i18n-composable'
import { useSearchIAStore } from '@/modules/documentSearcher/stores/useSearchIAStore'
import useCustomDialog from '@/composables/useCustomDialog'
import { TrackerEvents, trackEvent } from '@/plugins/tracker'

// STORE
const searchIAStore = useSearchIAStore()
const { showCustomDialog } = useCustomDialog()

// PROPS
const props = withDefaults(
  defineProps<{
    content: string
    htmlContent?: string
    filename?: string
    emailSubject?: string
  }>(),
  {
    htmlContent: '',
    filename: 'documento',
    emailSubject: ''
  }
)

// TRANSLATIONS
const { t } = useI18n()
const dialogModule = ModuleNamespaces.DIALOG
const TEXT = {
  copySuccess: t('components.alerts.copied_consultation') as string,
  errorCopy: t('components.alerts.copied_consultation_error') as string,
  copy: t('action_buttons.copy') as string,
  downloadTxt: t('action_buttons.download_txt') as string,
  email: t('action_buttons.send_by_email') as string,
  print: t('action_buttons.print') as string,
  like: t('action_buttons.like_response') as string,
  dislike: t('action_buttons.dislike_response') as string,
  likeSuccess: t('views.search_ia.likeSuccess') as string,
  genericError: t('views.search_ia.genericError') as string
}

// URLs de los iconos
const ICON = {
  copy: 'https://assets.lefebvre.es/media/icons-2/svg/icon-copy.svg',
  download: 'https://assets.lefebvre.es/media/icons-2/svg/icon-download.svg',
  email: 'https://assets.lefebvre.es/media/icons-2/svg/icon-send-arrow.svg',
  print: 'https://assets.lefebvre.es/media/icons-2/svg/icon-print.svg',
  like: 'https://assets.lefebvre.es/media/icons-2/svg/icon-like.svg',
  dislike: 'https://assets.lefebvre.es/media/icons-2/svg/icon-dislike.svg'
}

// METHODS
const trackActionClicked = (action: string) => {
  trackEvent(TrackerEvents.AI_SEARCH_ACTION_CLICKED, {
    action_button_ia_clicked: action,
    search_id: searchIAStore.search_id
  })
}

const showCopySuccessDialog = () => {
  store.dispatch(
    `${dialogModule}/showDialog`,
    {
      type: DialogTypes.SUCCESS,
      message: TEXT.copySuccess
    },
    { root: true }
  )
}

const showCopyErrorDialog = () => {
  store.dispatch(
    `${dialogModule}/showDialog`,
    {
      type: DialogTypes.ERROR,
      message: TEXT.errorCopy
    },
    { root: true }
  )
}

// Método alternativo para copiar al portapapeles
const fallbackCopyTextToClipboard = () => {
  try {
    // Crear un elemento temporal
    const textArea = document.createElement('textarea')
    textArea.value = props.content

    // Hacer que el textarea sea invisible pero parte del DOM
    textArea.style.position = 'fixed'
    textArea.style.top = '0'
    textArea.style.left = '0'
    textArea.style.width = '2em'
    textArea.style.height = '2em'
    textArea.style.padding = '0'
    textArea.style.border = 'none'
    textArea.style.outline = 'none'
    textArea.style.boxShadow = 'none'
    textArea.style.background = 'transparent'

    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    // Intentar usar el comando de documento
    const successful = document.execCommand('copy')

    // Limpiar
    document.body.removeChild(textArea)

    if (successful) {
      showCopySuccessDialog()
    } else {
      showCopyErrorDialog()
    }
  } catch (_err) {
    showCopyErrorDialog()
  }
}

// Copia el contenido al portapapeles
const copyContent = () => {
  trackActionClicked('copy')
  try {
    // Intenta usar la API Clipboard moderna
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(props.content)
        .then(() => {
          showCopySuccessDialog()
        })
        .catch(() => {
          // Si falla, usa el método alternativo
          fallbackCopyTextToClipboard()
        })
    } else {
      // Método alternativo si la API no está disponible
      fallbackCopyTextToClipboard()
    }
  } catch (_err) {
    showCopyErrorDialog()
    fallbackCopyTextToClipboard()
  }
}

// Descarga el contenido como un archivo de texto
const downloadContent = () => {
  trackActionClicked('download')
  const fileName = `${props.filename}.txt`
  const blob = new Blob([props.content], { type: 'text/plain;charset=utf-8' })

  // Crear un enlace temporal para la descarga
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// Abre el cliente de correo con el contenido
const emailContent = () => {
  trackActionClicked('email')
  const subject = encodeURIComponent(props.emailSubject || props.filename)
  const body = encodeURIComponent(props.content)
  window.open(`mailto:?subject=${subject}&body=${body}`, '_blank')
}

// Imprime el contenido
const printContent = () => {
  trackActionClicked('print')
  const printWindow = window.open('', '_blank')
  if (printWindow) {
    printWindow.document.write(`
          <html>
            <head>
              <title>${props.filename}</title>
              <style>
                body {
                  font-family: Arial, sans-serif;
                  line-height: 1.6;
                  padding: 20px;
                }
                h1, h2, h3 { margin-top: 20px; }
              </style>
              <script>
                // Cerrar ventana después de imprimir
                window.addEventListener('afterprint', function() {
                  window.close();
                });

                // Método de respaldo por si el evento afterprint no se activa
                var hasPrinted = false;
                window.addEventListener('focus', function() {
                  // Si ya imprimió y ahora la ventana recupera el foco, cerrarla
                  if (hasPrinted) {
                    window.close();
                  }
                });
              <\/script>
            </head>
            <body>
              ${props.htmlContent || props.content.replace(/\n/g, '<br>')}
            </body>
          </html>
        `)
    printWindow.document.close()
    printWindow.print()

    printWindow.hasPrinted = true
  }
}

const likeContent = async () => {
  trackActionClicked('like')
  try {
    const question = searchIAStore.lastQuery

    await searchIAStore.likeAdvanceSearch({
      question
    })

    store.dispatch(
      `${dialogModule}/showDialog`,
      {
        type: DialogTypes.SUCCESS,
        message: TEXT.likeSuccess
      },
      { root: true }
    )
  } catch (_error) {
    store.dispatch(
      `${dialogModule}/showDialog`,
      {
        type: DialogTypes.ERROR,
        message: TEXT.genericError
      },
      { root: true }
    )
  }
}

const dislikeContent = () => {
  trackActionClicked('dislike')
  showCustomDialog(CustomDialogComponentName.SEARCH_IA_DISLIKE)
}
</script>

<style lang="scss" scoped>
.action-toolbar {
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  margin-top: 8px;
  padding-top: 4px;
}

.toolbar-separator {
  width: 1px;
  background-color: $neutral-grey-300;
  align-self: stretch;
}

.action-button {
  background: transparent;
  border: none;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.1);
  }
}

.action-icon {
  width: 20px;
  height: 20px;
  filter: brightness(0) saturate(100%) invert(45%) sepia(12%) saturate(455%) hue-rotate(182deg) brightness(94%)
    contrast(87%);
  transition: filter 0.2s ease;
}

.action-button:hover .action-icon {
  filter: brightness(0) saturate(100%) invert(35%) sepia(12%) saturate(455%) hue-rotate(182deg) brightness(90%)
    contrast(95%);
}
</style>
